import { createAsyncThunk } from "@reduxjs/toolkit";
import { iStateMessage } from "@/customTypes/GenericReduxTypes";
import { curryGetThunkName } from "@/projectUtils/ReduxUtil";
import { httpStatusCodes } from "@/customTypes/NetworkTypes";
import { ticketSetup } from "@/services/api/cresivoxServer";
import {
  REDUCER_NAME,
  ICreateTicketDTO,
  IReplyTicketDTO,
  IGetAllTicketsDTO,
  IUpdateTicketStatusDTO,
  IUpdateTicketPriorityDTO,
  IAssignTicketDTO,
} from "./Types";

const curriedGetThunkName = curryGetThunkName(REDUCER_NAME);

// Payload creators return { data: { httpStatusCode, data, message } } on
// success (the double-`.data` unwrap convention this codebase's reducers
// read via `action.payload?.data?.data`), not the `iGenericResponse<T>`
// shape — using that as the fulfilled generic was a type/reality mismatch.
type TicketThunkResult = { data: { httpStatusCode: number; data: any; message?: string } } | null;

export const createTicket = createAsyncThunk<
  TicketThunkResult,
  ICreateTicketDTO,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("createTicket"),
  async (payload, { rejectWithValue }) => {
    try {
      const response = await ticketSetup.createTicket(payload);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const getMyTickets = createAsyncThunk<
  TicketThunkResult,
  { page?: number; pageSize?: number } | undefined,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getMyTickets"),
  async (params, { rejectWithValue }) => {
    try {
      const response = await ticketSetup.getMyTickets(params?.page, params?.pageSize);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const getAllTickets = createAsyncThunk<
  TicketThunkResult,
  IGetAllTicketsDTO | undefined,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getAllTickets"),
  async (params, { rejectWithValue }) => {
    try {
      const response = await ticketSetup.getAllTickets(params);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const getTicketStats = createAsyncThunk<
  TicketThunkResult,
  void,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getTicketStats"),
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketSetup.getStats();
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const getTicketById = createAsyncThunk<
  TicketThunkResult,
  string,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getTicketById"),
  async (id, { rejectWithValue }) => {
    try {
      const response = await ticketSetup.getTicketById(id);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const replyToTicket = createAsyncThunk<
  TicketThunkResult,
  IReplyTicketDTO,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("replyToTicket"),
  async ({ id, message, attachment }, { rejectWithValue }) => {
    try {
      const response = await ticketSetup.replyToTicket(id, message, attachment);
      if (response) {
        const { httpStatusCode, data, message: msg } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message: msg } };
        }
        return rejectWithValue({ httpStatusCode, message: msg });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const updateTicketStatus = createAsyncThunk<
  TicketThunkResult,
  IUpdateTicketStatusDTO,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("updateTicketStatus"),
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await ticketSetup.updateTicketStatus(id, status);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const updateTicketPriority = createAsyncThunk<
  TicketThunkResult,
  IUpdateTicketPriorityDTO,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("updateTicketPriority"),
  async ({ id, priority }, { rejectWithValue }) => {
    try {
      const response = await ticketSetup.updateTicketPriority(id, priority);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const assignTicket = createAsyncThunk<
  TicketThunkResult,
  IAssignTicketDTO,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("assignTicket"),
  async ({ id, assignedAgentId }, { rejectWithValue }) => {
    try {
      const response = await ticketSetup.assignTicket(id, assignedAgentId);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);
